import { useEffect, useMemo } from 'react'
import { Select } from 'src/components'
import { Chart, AxisOptions } from 'react-charts'
import { savedChartMonth, savedChartYear, savedChartWeek } from './storage'
import {
  ChartElement,
  getMonthChartItems,
  getMonthChartLabel,
  getMonthChartTooltip,
  getMonthNameByIndex,
  getWeekChartItems,
  getWeekChartLabel,
  getWeekChartTooktip,
  getWeekName,
  getYearChartItems,
  getYearChartLabel,
  groupChartItems,
} from './chartUtils'

type Props = {
  items: ChartElement[]
}

function ChartUsers(props: Props) {
  const [year, setYear] = savedChartYear.useState('')
  const [month, setMonth] = savedChartMonth.useState('')
  const [week, setWeek] = savedChartWeek.useState('')

  const groups = useMemo(() => {
    return groupChartItems(props.items)
  }, [props.items])

  useEffect(() => {
    if (groups) {
      setYear((p) =>
        !p ? Object.keys(groups)[Object.keys(groups).length - 1] : p,
      )
    }
  }, [groups, setYear])

  const yearOptions = useMemo(() => {
    if (!groups) return []
    return Object.keys(groups).map((k) => ({
      name: k,
      key: k,
    }))
  }, [groups])

  const monthOptions = useMemo(() => {
    if (!groups || !year) return []
    return Object.keys(groups[year]).map((k) => ({
      name: getMonthNameByIndex(+k - 1),
      key: k,
    }))
  }, [groups, year])

  const weekOptions = useMemo(() => {
    if (!groups || !year || !month) return []
    return Object.keys(groups[year][month]).map((k) => ({
      name: getWeekName(+year, +month, +k),
      key: k,
    }))
  }, [groups, year, month])

  const viewType = useMemo(() => {
    if (year && month && week) return 'WEEK'
    if (year && month) return 'MONTH'
    if (year) return 'YEAR'
    return 'NOT_SELECT'
  }, [year, month, week])

  const availableYear = useMemo(() => {
    if (!groups || !year) return false
    return !!groups[year]
  }, [groups, year])

  const availableMonth = useMemo(() => {
    if (!groups || !year || !month) return false
    return !!groups[year]?.[month]
  }, [groups, year, month])

  const availableWeek = useMemo(() => {
    if (!groups || !year || !month || !week) return false
    return !!groups[year]?.[month]?.[week]
  }, [groups, year, month, week])

  const [items, maxAmount] = useMemo(() => {
    if (!groups || viewType === 'NOT_SELECT') return [[], 0]
    if (viewType === 'YEAR' && !availableYear) return [[], 0]
    if (viewType === 'MONTH' && !availableMonth) return [[], 0]
    if (viewType === 'WEEK' && !availableWeek) return [[], 0]

    let data: {
      items: {
        label: string
        amount: number
      }[]
      maxAmount: number
    } | null

    switch (viewType) {
      case 'YEAR':
        data = getYearChartItems(groups, +year)
        break
      case 'MONTH':
        data = getMonthChartItems(groups, +year, +month)
        break
      default:
        data = getWeekChartItems(groups, +year, +month, +week)
    }

    return [
      [
        {
          label: 'Hours logged in',
          data: data.items,
        },
      ],
      data.maxAmount,
    ]
  }, [
    week,
    year,
    month,
    groups,
    viewType,
    availableYear,
    availableWeek,
    availableMonth,
  ])

  const primaryAxis = useMemo(
    (): AxisOptions<any> => ({
      getValue: (datum) => datum.label,
      showGrid: false,
      formatters: {
        scale: (date: Date) => {
          switch (viewType) {
            case 'YEAR':
              return getYearChartLabel(date)
            case 'MONTH':
              return getMonthChartLabel(date)
            case 'WEEK':
              return getWeekChartLabel(date)
            default:
              return date.toString()
          }
        },
        tooltip: (date: Date) => {
          switch (viewType) {
            case 'YEAR':
              return getYearChartLabel(date)
            case 'MONTH':
              return getMonthChartTooltip(date)
            case 'WEEK':
              return getWeekChartTooktip(date)
            default:
              return date.toString()
          }
        },
      },
    }),
    [viewType],
  )

  const secondaryAxes = useMemo(
    (): AxisOptions<any>[] => [
      {
        getValue: (datum) => datum.amount,
        elementType: 'area',
        showDatumElements: false,
        tickCount: maxAmount < 10 ? maxAmount : 4,
      },
    ],
    [maxAmount],
  )

  const availableView =
    ((viewType === 'YEAR' && availableYear) ||
      (viewType === 'MONTH' && availableMonth) ||
      (viewType === 'WEEK' && availableWeek)) &&
    !!items?.[0]?.data

  return !!groups ? (
    <div>
      <div className="grid grid-cols-3 gap-6">
        <Select
          key={year ? 'selected' : 'not'}
          id="year"
          options={yearOptions}
          onChange={(value) => {
            setYear(value)
            setMonth('')
            setWeek('')
          }}
          title="Year"
          value={year}
        />
        <Select
          key={year}
          id="month"
          disabled={!availableYear}
          options={monthOptions}
          onChange={(value) => {
            setMonth(value)
            setWeek('')
          }}
          title="Month"
          value={month}
        />
        <Select
          key={`${year}-${month}`}
          id="week"
          disabled={!availableMonth}
          options={weekOptions}
          onChange={(value) => {
            setWeek(value)
          }}
          title="Week"
          value={week}
        />
      </div>
      <div style={{ height: 600 }}>
        {availableView ? (
          <Chart
            key={`${year}-${month}-${week}`}
            options={{
              data: items,
              primaryAxis,
              secondaryAxes,
            }}
          />
        ) : null}
      </div>
    </div>
  ) : null
}

export default ChartUsers
